import { useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

// comps
import { DialogContent, Step, TextField, Typography, Tooltip, Box } from '@mui/material';
import DialogTemplate from './dialogTemplate';
import Stepper from '../global/stepper';
import GlobalInput from '../global/input';
import GlobalButton from '../global/button';
import GlobalDropdown from '../global/dropdown';
import ToastMessage from '../global/toastMessage';
import Label from '../global/label';

// icons
import { ReactComponent as TimeIcon } from '../../assets/icons/time-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as GroupIcon } from '../../assets/icons/group-icon.svg';
import { ReactComponent as NotesIcon } from '../../assets/icons/notes-icon.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { ReactComponent as KeyboardIcon } from '../../assets/icons/keyboard-icon.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';

// constants
import { colors } from '../../theme';
import { MOMENT_DATE_FULL_FORMAT, MOMENT_DATE_FORMAT, MOMENT_TIME_FORMAT, MOMENT_TIME_DISPLAY_FORMAT } from '../../constants/date_format';

interface DialogOptionProps {
    selectedOption: boolean;
    disabled?: boolean;
}

interface ClassNameProps {
    name?: string;
    isEdit?: boolean;
    flexStart?: boolean;
}

const OfficeHoursDialog = (props) => {
    const { defaultTimeIn, termStartDate, termEndDate, onClose, onSubmit, termCode, isEdit, officeHourData, weekdays, isSubmitDisabled } = props;

    const [activeStep, setActiveStep] = useState(isEdit ? 3 : 0);

    const backgroundColor = isEdit || activeStep === 4 ? '#37CA83' : '#007cde';

    const [requiredFields, setRequiredFields] = useState([]);
    const [errorMsg, setErrorMsg] = useState(null);

    const {
        id,
        appointmentRequired,
        isRecurring,
        isOnline,
        dayOfWeek,
        startDate,
        endDate,
        timeIn,
        timeOut,
        publicNote,
        appointmentNote,
        appointmentSlotCount,
        timeSlotLengthMinutes,
        meetingUrl,
        date,
        meetingLocation,
        bookedSlotsCount
    } = officeHourData;

    const defaultFieldsState = {
        meetingLocation: false,
        appointmentSlotCount: false,
        meetingUrl: false,
    };

    const today = moment().format(MOMENT_DATE_FORMAT);

    const handleDate = (date: string) => {
        return moment(date).format(MOMENT_DATE_FORMAT);
    };

    const handleTime = (time: string) => {
        return moment(time, MOMENT_TIME_FORMAT).format(MOMENT_TIME_FORMAT);
    };

    const pastTermStartDate = today < handleDate(termStartDate);

    const minTime = handleTime('08:30:00');
    const maxTime = handleTime('19:30:00');
    const minDate = pastTermStartDate ? handleDate(termStartDate) : today;
    const maxDate = handleDate(termEndDate);

    // Keep track of fields the user has entered data for.
    const [focusedFields, setFocusedFields] = useState(defaultFieldsState);

    const [data, setData] = useState({
        isRecurring: isEdit ? isRecurring : null,
        id: isEdit && id ? id : 0,
        appointmentRequired: isEdit ? appointmentRequired : null,
        isOnline: isEdit ? isOnline : null,
        dayOfWeek: isEdit ? dayOfWeek : 'Monday',
        timeIn: isEdit ? timeIn : handleTime(defaultTimeIn),
        timeOut: isEdit ? timeOut : moment(defaultTimeIn, MOMENT_TIME_FORMAT).add(1, 'hour').format(MOMENT_TIME_FORMAT),
        startDate: isEdit ? handleDate(startDate) : minDate,
        endDate: isEdit ? handleDate(endDate) : handleDate(termEndDate),

        appointmentSlotCount: isEdit ? appointmentSlotCount : null,
        timeSlotLengthMinutes: isEdit ? timeSlotLengthMinutes : null,
        meetingLocation: isEdit ? meetingLocation : null,
        meetingUrl: isEdit ? meetingUrl : null,
        publicNote: isEdit ? publicNote : null,
        appointmentNote: isEdit ? appointmentNote : null,
    });

    /*
     - Validation is only run when the user updated a field. Navigating through the steps should not trigger validation.
     */
    useEffect(() => {
        if (activeStep === 3) {
            validateData(focusedFields);
        }
    }, [data]);

    useEffect(() => {
        const generateOfficeHoursNote = () => {
            if (isEdit) return publicNote;

            let note = 'Office Hours';

            if (data.isOnline === false && data.appointmentRequired === false) note = 'Drop-In';
            if (data.isOnline === true && data.appointmentRequired === true) note = 'Online Appointment';
            if (data.isOnline === false && data.appointmentRequired === true) note = 'In-person Appointment';

            return note;
        };

        if (activeStep === 3) {
            setFocusedFields(defaultFieldsState);

            if (!isEdit && !data.publicNote) {
                setData({
                    ...data,
                    publicNote: isEdit ? publicNote : generateOfficeHoursNote(),
                });
            }
        }
    }, [activeStep]);

    const steps = [
        {
            label: 'Frequency',
            data: data.isRecurring ? 'Recurring' : data.isRecurring === null ? null : 'One-day',
        },
        {
            label: 'Delivery Method',
            data: data.isOnline ? 'Online' : data.isOnline === null ? null : 'In-person',
        },
        {
            label: 'Meeting Type',
            data: data.appointmentRequired ? 'Appointment-Based' : data.appointmentRequired === null ? null : 'Drop-in',
        },
        {
            label: 'Office Hour Details',
            data: '',
        },
        {
            label: 'Confirmation',
            data: '',
        },
    ];

    const handleChange = (name: string, value: any) => {
        if (value === '') value = null;

        setData({
            ...data,
            // [name]: name === 'dayOfWeek' ? value.fullName : value,
            [name]: value,
        });

        setFocusedFields({
            ...focusedFields,
            [name]: true,
        });
    };

    /*
     - If the user clicks "Next", then it is assumed they have entered data (focused) for all the fields
     */
    const handleNext = () => {
        if (activeStep === 3) {
            let focusedState = { ...focusedFields };
            Object.keys(focusedFields).forEach((key) => (focusedState[key] = true));

            setFocusedFields(focusedState);
            const isValid = validateData(focusedState);

            if (!isValid) return;
            setData({
                ...data,
                dayOfWeek: data.dayOfWeek !== 'Monday' ? data.dayOfWeek.fullName : data.dayOfWeek,
            });
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
        //last screen
        else if (activeStep === 4) onSubmit(data);
        else setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };
    /*
     - if the user clicks "Back" from step 3 (entering data) then reset all the data for the fields
     */
    const handleBack = () => {
        if (activeStep === 3) {
            setData({
                ...data,
                dayOfWeek: isEdit ? dayOfWeek : 'Monday',
                id: isEdit && id ? id : 0,
                timeIn: handleTime(defaultTimeIn),
                timeOut: isEdit ? timeOut : moment(defaultTimeIn, MOMENT_TIME_FORMAT).add(1, 'hour').format(MOMENT_TIME_FORMAT),
                startDate: isEdit ? handleDate(startDate) : handleDate(termStartDate),
                endDate: isEdit ? handleDate(endDate) : handleDate(termEndDate),

                appointmentSlotCount: isEdit ? appointmentSlotCount : null,
                timeSlotLengthMinutes: isEdit ? timeSlotLengthMinutes : null,
                meetingLocation: isEdit ? meetingLocation : null,
                meetingUrl: isEdit ? meetingUrl : null,
                publicNote: isEdit ? publicNote : null,
                appointmentNote: isEdit ? appointmentNote : null,
            });
        }

        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleTimeAndDateChange = (name: string, value: any) => {
        if (name === 'timeIn' || name === 'timeOut') {
            const timeValue = handleTime(value);

            if (name === 'timeIn') {
                setData({
                    ...data,
                    [name]: timeValue,
                    timeOut: value ? moment(value, MOMENT_TIME_FORMAT).add(1, 'hour').format(MOMENT_TIME_FORMAT) : handleTime(data.timeOut),
                });
                return;
            }
            if (name === 'timeOut') {
                if (moment(value, MOMENT_TIME_FORMAT).isSameOrBefore(moment(data.timeIn, MOMENT_TIME_FORMAT))) {
                    setRequiredFields([...requiredFields, 'Time Out']);
                    setErrorMsg('Time out must be AFTER Time in.');
                    return;
                }
            }
            setData({ ...data, [name]: timeValue });
        }
        if (name === 'endDate') {
            if (moment(value).isSameOrBefore(moment(data.startDate))) {
                setRequiredFields([...requiredFields, 'End Date']);
                setErrorMsg('End Date must be AFTER Start Date.');
                return;
            }
            setData({ ...data, [name]: value });
        } else {
            setData({ ...data, [name]: value });
        }
    };

    const handleNumberOfSlotsChange = (numberOfSlots) => {
        const totalMinutes = moment(data.timeOut, MOMENT_TIME_FORMAT).diff(moment(data.timeIn, MOMENT_TIME_FORMAT), 'minutes');

        const totalSlots = totalMinutes / numberOfSlots;

        setData((state) => ({
            ...state,
            appointmentSlotCount: parseInt(numberOfSlots),
            timeSlotLengthMinutes: Math.round(totalSlots),
        }));

        setFocusedFields({
            ...focusedFields,
            appointmentSlotCount: true,
        });
    };

    /*
      - Highlights the invalid fields red
     */
    const validateData = (focusedFields) => {
        const requiredFields = [];

        if (!data.timeIn) requiredFields.push('Time In');
        if (!data.timeOut) requiredFields.push('Time Out');
        if (data.isRecurring) {
            if (data.startDate && data.endDate && moment(data.endDate).isSameOrBefore(moment(data.startDate))) {
                requiredFields.push('Start Date');
                requiredFields.push('End Date');
            }
            if (!data.dayOfWeek) {
                requiredFields.push('Day of Week');
            }
        }

        if (!data.isOnline && focusedFields.meetingLocation && !data.meetingLocation) requiredFields.push('Meeting Location');
        if (data.appointmentRequired && data.isOnline && focusedFields.meetingUrl && !data.meetingUrl) requiredFields.push('Virtual Meeting URL');
        if (data.appointmentRequired && focusedFields.appointmentSlotCount && !data.appointmentSlotCount) requiredFields.push('Number of slot(s)');
        if (data.publicNote === null) requiredFields.push('Public Notes');
        else {
            setErrorMsg('The highlighted fields are required in order to proceed.');
        }

        setRequiredFields(requiredFields);

        return requiredFields.length === 0;
    };

    const renderContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <DialogActions>
                        <DialogOption
                            selectedOption={data.isRecurring === false}
                            onClick={() => {
                                setData({
                                    ...data,
                                    isRecurring: false,
                                });
                                handleNext();
                            }}
                        >
                            <div className="select-circle" />
                            <div>
                                <Typography variant="h4">One-Day</Typography>
                                <Typography variant="body2">Create a time slot for Office Hours for one particular day</Typography>
                            </div>
                        </DialogOption>
                        <DialogOption
                            selectedOption={data.isRecurring}
                            onClick={() => {
                                setData({
                                    ...data,
                                    isRecurring: true,
                                });
                                handleNext();
                            }}
                        >
                            <div className="select-circle" />
                            <div>
                                <Typography variant="h4">Recurring</Typography>
                                <Typography variant="body2">Create multiple time slots for Office Hours for a certain period</Typography>
                            </div>
                        </DialogOption>
                    </DialogActions>
                );
            case 1:
                return (
                    <DialogActions>
                        <DialogOption
                            selectedOption={data.isOnline}
                            onClick={() => {
                                setData({
                                    ...data,
                                    isOnline: true,
                                });
                                handleNext();
                            }}
                        >
                            <div className="select-circle" />
                            <div>
                                <Typography variant="h4">Online</Typography>
                                <Typography variant="body2">Through a Teams meeting</Typography>
                            </div>
                        </DialogOption>
                        <DialogOption
                            selectedOption={data.isOnline === false}
                            onClick={() => {
                                setData({
                                    ...data,
                                    isOnline: false,
                                });
                                handleNext();
                            }}
                        >
                            <div className="select-circle" />
                            <div>
                                <Typography variant="h4">In-person</Typography>
                                <Typography variant="body2">On campus</Typography>
                            </div>
                        </DialogOption>
                    </DialogActions>
                );
            case 2:
                return (
                    <DialogActions>
                        <DialogOption
                            selectedOption={data.appointmentRequired}
                            onClick={() => {
                                setData({
                                    ...data,
                                    appointmentRequired: true,
                                });
                                handleNext();
                            }}
                        >
                            <div className="select-circle" />
                            <div>
                                <Typography variant="h4">Appointment-Based</Typography>
                                <Typography variant="body2">Students attending these sessions must book a time slot prior to their visit</Typography>
                            </div>
                        </DialogOption>
                        <DialogOption
                            selectedOption={data.appointmentRequired === false}
                            disabled={data.isOnline}
                            onClick={() => {
                                if (data.isOnline) return;
                                else {
                                    setData({
                                        ...data,
                                        appointmentRequired: false,
                                    });
                                    handleNext();
                                }
                            }}
                        >
                            <div className="select-circle" />
                            <div>
                                <Typography variant="h4">Drop-In</Typography>
                                <Typography variant="body2">Students attending these sessions can drop in at any time within the given time period</Typography>
                            </div>
                        </DialogOption>
                    </DialogActions>
                );
            //    Step 3 is used to both add and update
            case 3:
                return (
                    <DialogContentContainer className="dialog-content">
                        {requiredFields?.length > 0 && errorMsg && <ToastMessage isError icon={<WarningIcon />} msg={errorMsg} />}
                        <ToastMessage
                            icon={<InfoIcon />}
                            msg={
                                <Note>
                                    You can either use <KeyboardIcon /> or <KeyboardIcon /> keys, or manually type in values to change Date and Time, including AM/PM.
                                </Note>
                            }
                        />
                        <>
                            {!isEdit && data.isRecurring && (
                                <Box sx={{ mt: '1em' }}>
                                    <GlobalDropdown
                                        isRequired
                                        label={'Day of Week'}
                                        isError={requiredFields?.includes('Day of Week')}
                                        placeholder={'Monday'}
                                        data={weekdays}
                                        field={'id'}
                                        name={'fullName'}
                                        dataItemKey={'dayOfWeek'}
                                        value={data.dayOfWeek}
                                        onChange={handleChange}
                                    />
                                </Box>
                            )}
                            <DialogActions>
                                {(data.isRecurring === false || isEdit) && (
                                    <>
                                        <GlobalInput
                                            isRequired
                                            label={'date'}
                                            isError={requiredFields?.includes('Start Date')}
                                            type={'date'}
                                            value={data.startDate}
                                            disabled={isEdit && bookedSlotsCount}
                                            name={'startDate'}
                                            inputProps={{ min: minDate, max: maxDate }}
                                            onChange={handleTimeAndDateChange}
                                        />
                                    </>
                                )}
                                {!isEdit && data.isRecurring && (
                                    <>
                                        <FieldContainer>
                                            <GlobalInput
                                                isRequired
                                                label={'Start Date'}
                                                isError={requiredFields?.includes('Start Date')}
                                                type={'date'}
                                                value={data.startDate}
                                                name={'startDate'}
                                                inputProps={{ min: minDate, max: maxDate }}
                                                onChange={handleTimeAndDateChange}
                                            />
                                        </FieldContainer>
                                        <FieldContainer>
                                            <GlobalInput
                                                isRequired
                                                isError={requiredFields?.includes('End Date')}
                                                label={'End Date'}
                                                type={'date'}
                                                value={data.endDate}
                                                name={'endDate'}
                                                inputProps={{ min: minDate, max: maxDate }}
                                                onChange={handleTimeAndDateChange}
                                            />
                                        </FieldContainer>
                                    </>
                                )}
                                <div>
                                    <FlexContainer>
                                        <FieldContainer>
                                            <GlobalInput
                                                isRequired
                                                label={'Time In'}
                                                isError={requiredFields?.includes('Time In')}
                                                type={'time'}
                                                disabled={isEdit && bookedSlotsCount}
                                                value={data.timeIn}
                                                name={'timeIn'}
                                                inputProps={{ min: minTime, max: maxTime }}
                                                onChange={handleTimeAndDateChange}
                                                sx={{ width: '122px' }}
                                            />
                                        </FieldContainer>
                                        <div>
                                            <GlobalInput
                                                isRequired
                                                label={'Time Out'}
                                                isError={requiredFields?.includes('Time Out')}
                                                type={'time'}
                                                disabled={isEdit && bookedSlotsCount}
                                                value={data.timeOut}
                                                name={'timeOut'}
                                                inputProps={{ min: minTime, max: maxTime }}
                                                onChange={handleTimeAndDateChange}
                                                sx={{ width: '122px' }}
                                            />
                                        </div>
                                    </FlexContainer>
                                </div>
                            </DialogActions>

                            <FlexBox sx={{ mb: !data.appointmentRequired && '1em' }}>
                                <div>
                                    {data.isOnline ? (
                                        <GlobalInput
                                            isRequired
                                            label={'Virtual Meeting URL'}
                                            isError={requiredFields?.includes('Virtual Meeting URL')}
                                            value={data.meetingUrl}
                                            name={'meetingUrl'}
                                            onChange={handleChange}
                                        />
                                    ) : (
                                        <GlobalInput
                                            isRequired
                                            label={'Meeting Location'}
                                            isError={requiredFields?.includes('Meeting Location')}
                                            value={data.meetingLocation}
                                            disabled={isEdit}
                                            name={'meetingLocation'}
                                            onChange={handleChange}
                                        />
                                    )}
                                </div>

                                <Tooltip title="This will only be available to bookers" arrow>
                                    <InfoIcon />
                                </Tooltip>
                            </FlexBox>

                            {data.appointmentRequired ? (
                                <DialogActions name="appt">
                                    <FlexContainer sx={{ mb: '1em' }}>
                                        <GlobalInput
                                            isRequired
                                            label={'Number of Slot(s)'}
                                            isError={requiredFields?.includes('Number of slot(s)')}
                                            type={'number'}
                                            parse={'int'}
                                            value={data.appointmentSlotCount}
                                            disabled={isEdit}
                                            name={'appointmentSlotCount'}
                                            onChange={(name: string, value: number) => handleNumberOfSlotsChange(value)}
                                            sx={{ mr: '1em' }}
                                            inputProps={{ min: 0 }}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Label label={'Minutes per slot'} />
                                            <Typography sx={{ color: colors.blue.main }}> {!data.timeSlotLengthMinutes ? '0' : data.timeSlotLengthMinutes} (Auto-generated)</Typography>
                                        </Box>
                                    </FlexContainer>
                                    <FlexBox>
                                        <GlobalInput
                                            isRequired
                                            label={'Public Notes'}
                                            isError={requiredFields?.includes('Public Notes')}
                                            value={data.publicNote}
                                            name={'publicNote'}
                                            onChange={handleChange}
                                            inputProps={{
                                                maxLength: 50,
                                            }}
                                        />

                                        <Tooltip
                                            title={
                                                <span
                                                    style={{ whiteSpace: 'pre-line' }}
                                                >{`This will appear in the public timetables cell. \nMax 50 characters. \nIf you have a general note to add, please go to \nData Management > Instructors > Edit > Public Note field to add one.`}</span>
                                            }
                                            arrow
                                        >
                                            <InfoIcon />
                                        </Tooltip>
                                    </FlexBox>
                                    <FlexBox sx={{ mt: '1em' }}>
                                        <GlobalInput label={'Appointment Details'} value={data.appointmentNote} name={'appointmentNote'} onChange={handleChange} />
                                        <Tooltip title="This will only be available to bookers" arrow>
                                            <InfoIcon />
                                        </Tooltip>
                                    </FlexBox>
                                </DialogActions>
                            ) : (
                                <FlexBox>
                                    <GlobalInput
                                        isRequired
                                        label={'Public Notes'}
                                        value={data.publicNote}
                                        name={'publicNote'}
                                        onChange={handleChange}
                                        inputProps={{
                                            maxLength: 50,
                                        }}
                                    />
                                    <Tooltip
                                        title={
                                            <span
                                                style={{ whiteSpace: 'pre-line' }}
                                            >{`This will appear in the public timetables cell. \nMax 50 characters. \nIf you have a general note to add, please go to \nData Management > Instructors > Edit > Public Note field to add one.`}</span>
                                        }
                                        arrow
                                    >
                                        <InfoIcon />
                                    </Tooltip>
                                </FlexBox>
                            )}
                        </>
                        {/* )} */}
                    </DialogContentContainer>
                );
        }
    };

    const isNextStepAllowed = (step, data) => {
        if (isSubmitDisabled) return true;
        if (step === 0 && data.isRecurring === null) return true;
        if (step === 1 && data.isOnline === null) return true;
        if (step === 2 && data.appointmentRequired === null) return true;
        return false;
    };

    const renderBaseContent = () => {
        return (
            <>
                <StyledDialogContent name="content" flexStart={activeStep === 3} className="dialog-content">
                    <FlexContainer>
                        <div style={{ width: '100%' }}>
                            {!isEdit && <Stepper steps={steps} activeStep={activeStep} />}
                            {activeStep === 4 ? (
                                <FlexContainer name="column">
                                    <DialogContentContainer name="confirm">
                                        {data.appointmentRequired && <ToastMessage icon={<InfoIcon />} msg={'Whenever a slot is booked or cancelled, you will be notified via email.'} />}
                                        <FlexContainer name="margin">
                                            <TimeIcon />
                                            {!isEdit && data.isRecurring && (
                                                <>
                                                    <div>
                                                        <ConfirmTitle variant="body2">Day of Week</ConfirmTitle>
                                                        <Typography>{data.dayOfWeek}</Typography>
                                                    </div>
                                                    <div>
                                                        <ConfirmTitle variant="body2">{'Start Date'}</ConfirmTitle>
                                                        <Typography>{handleDate(data.startDate)}</Typography>
                                                    </div>
                                                    <div>
                                                        <ConfirmTitle variant="body2">{'End Date'}</ConfirmTitle>
                                                        <Typography>{handleDate(data.endDate)}</Typography>
                                                    </div>
                                                </>
                                            )}

                                            {(data.isRecurring === false || isEdit) && (
                                                <>
                                                    <div>
                                                        <ConfirmTitle variant="body2">{'Date'}</ConfirmTitle>
                                                        <Typography>{moment(data.startDate).format(MOMENT_DATE_FULL_FORMAT)}</Typography>
                                                    </div>
                                                </>
                                            )}

                                            <div>
                                                <ConfirmTitle variant="body2">Time In</ConfirmTitle>
                                                <Typography>{moment(data.timeIn, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}</Typography>
                                            </div>

                                            <div>
                                                <ConfirmTitle variant="body2">Time Out</ConfirmTitle>
                                                <Typography>{moment(data.timeOut, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}</Typography>
                                            </div>
                                        </FlexContainer>

                                        <FlexContainer name="margin">
                                            <LocationIcon />
                                            <div>
                                                <ConfirmTitle variant="body2">{data.isOnline ? `Virtual Meeting Link` : `Meeting Location`}</ConfirmTitle>
                                                <Typography>{data.isOnline ? data.meetingUrl : data.meetingLocation}</Typography>
                                            </div>
                                        </FlexContainer>

                                        {data.appointmentRequired && (
                                            <FlexContainer name="margin">
                                                <GroupIcon />
                                                <div>
                                                    <ConfirmTitle variant="body2">Number of Slot(s)</ConfirmTitle>
                                                    <Typography>{data.appointmentSlotCount}</Typography>
                                                </div>
                                                <div>
                                                    <ConfirmTitle variant="body2">Minutes per Slot</ConfirmTitle>
                                                    <Typography>{data.timeSlotLengthMinutes}</Typography>
                                                </div>
                                            </FlexContainer>
                                        )}
                                        {(data.publicNote || data.appointmentNote) && (
                                            <FlexContainer name="margin">
                                                <NotesIcon />
                                                {data.publicNote && (
                                                    <div>
                                                        <ConfirmTitle variant="body2">Notes</ConfirmTitle>
                                                        <Typography>{data.publicNote}</Typography>
                                                    </div>
                                                )}
                                                {data.appointmentNote && (
                                                    <div>
                                                        <ConfirmTitle variant="body2">Appointment Details</ConfirmTitle>
                                                        <Typography>{data.appointmentNote}</Typography>
                                                    </div>
                                                )}
                                            </FlexContainer>
                                        )}
                                    </DialogContentContainer>
                                </FlexContainer>
                            ) : (
                                <StyledDialogContent flexStart={activeStep === 3} className="dialog-content">
                                    {renderContent(activeStep)}
                                </StyledDialogContent>
                            )}
                            <DialogActions name="bottom" isEdit={isEdit} className="dialog-content">
                                {!isEdit && <GlobalButton variant="outlined" text="back" disabled={activeStep === 0} onClick={handleBack} />}
                                <GlobalButton
                                    variant="contained"
                                    name="next"
                                    backgroundColor={backgroundColor}
                                    text={activeStep === 4 ? (isEdit ? 'Update' : 'Create') : 'Next'}
                                    onClick={handleNext}
                                    disabled={isNextStepAllowed(activeStep, data)}
                                />
                            </DialogActions>
                        </div>
                    </FlexContainer>
                </StyledDialogContent>
            </>
        );
    };

    return (
        <DialogTemplate
            {...props}
            maxWidth={activeStep !== 3 || data.isRecurring === false ? 'md' : 'lg'}
            title={isEdit ? `Update Office Hours` : `Create Office Hours`}
            renderContent={renderBaseContent}
        />
    );
};

export default OfficeHoursDialog;

const StyledDialogContent = styled(DialogContent)<ClassNameProps>`
    && {
        padding: 0px;
        /* min-height: 345px; */
        min-height: 250px;
        display: flex;
        flex-direction: column;
        /* justify-content: ${(props) => (props.flexStart ? 'flex-start' : 'center')}; */
        justify-content: center;
    }
`;

const DialogContentContainer = styled.div<ClassNameProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-around;
    margin: 1em 0;
    /* overflow: hidden; */

    ${(props) =>
        props.name === 'confirm' &&
        `
        margin: 30px 0px 40px 0px;
        img {
            width: 38px;
            height: auto;
            margin-right: 15px;
        }
        div {
            margin-right: 30px;
        }

        svg {
            margin-right: 10px;
            width: 20px;
            height: 20px; 
            path {
                fill: ${colors.blue.main};
            }
        }
        `}
`;

const FlexContainer = styled(Box)<ClassNameProps>`
    && {
        display: flex;
        align-items: center;
        /* justify-content: center; */

        ${(props) =>
            props.name === 'column' &&
            `
                flex-direction: column;
        `}

        ${(props) =>
            props.name === 'margin' &&
            `
                margin: 10px 0px;
        `}
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        div:first-of-type {
            flex: 10;
        }

        svg {
            width: 20px;
            height: 20px;
            margin-left: 10px;

            path {
                fill: ${colors.blue.main};
            }
        }
    }
`;

const DialogActions = styled.div<ClassNameProps>`
    display: flex;
    min-width: 740px;

    justify-content: ${(props) => (props.isEdit ? 'center' : 'space-between')};

    margin: 1em 0px;
    width: 100%;

    ${(props) =>
        props.name === 'appt' &&
        `
            margin-top: 20px;
            flex-direction: column;
    `}
`;

const DialogOption = styled.div<DialogOptionProps>`
    border: ${(props) => (!props.selectedOption || props.disabled ? '1px solid #EAEAEA' : '1px solid #007cde')};
    border-radius: 10px;
    padding: 15px;
    display: flex;
    align-items: center;
    width: 320px;
    color: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.26)' : '#000000')};

    &:hover {
        cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
        border: ${(props) => (props.disabled ? '1px solid #EAEAEA' : '1px solid #007cde')};
    }

    .select-circle {
        border-radius: 50%;
        min-width: 18px;
        min-height: 18px;
        border: ${(props) => (!props.selectedOption || props.disabled ? '1px solid #EAEAEA' : `4px solid ${colors.blue.main}`)};
        margin-right: 10px;
    }

    p {
        margin-top: 0.5em;
    }
`;

const FieldContainer = styled.div`
    margin-right: 10px;
`;

const Note = styled.span`
    svg {
        margin: 0px 5px;
    }

    svg:last-of-type {
        transform: rotate(180deg);
    }
`;

const ConfirmTitle = styled(Typography)`
    && {
        color: #007cde;
    }
`;
